// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, OnInit, OnDestroy, ViewChild, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, Validators, ReactiveFormsModule, ValidatorFn, AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseModalComponent, BaseModalConfig } from '@lfx/shared/components/base-modal';
import { spaceValidator } from '@lfx/shared/validators';
import { KeyProjectsService } from '@lfx/features/key-projects/services/key-projects.service';


@Component({
  selector: 'lfx-rename-workspace-modal',
  imports: [CommonModule, ReactiveFormsModule, BaseModalComponent],
  template: `
    <lfx-base-modal
      [config]="modalConfig"
      [isLoading]="isLoading"
      [errorMessage]="errorMessage"
      (confirmed)="onSubmit()"
      (cancelled)="onCancel()">

      <form [formGroup]="renameWorkspaceForm" (ngSubmit)="onSubmit()">
        <div class="form-group">
          <input
            type="text"
            id="workspaceName"
            class="form-control lf-form-control-large"
            [class.is-invalid]="!!getControlErrors(workspaceNameControl)"
            formControlName="workspaceName"
            placeholder="Enter workspace name"
            [disabled]="isLoading"
          />
          <!-- Validation Errors -->
          <ng-container *ngIf="getControlErrors(workspaceNameControl) as errors">
            <div class="invalid-feedback" *ngIf="errors['required'] || errors['spaceError']">
              Workspace name is required.
            </div>
            <div class="invalid-feedback" *ngIf="errors['workspaceExists']">
              Workspace name already exists.
            </div>
            <div class="invalid-feedback" *ngIf="errors['unchanged']">
              Please enter a different name.
            </div>
          </ng-container>
        </div>
      </form>
    </lfx-base-modal>
  `,
  styleUrls: ['./rename-workspace-modal.component.scss']
})
export class RenameWorkspaceModalComponent implements OnInit, OnDestroy {
  @ViewChild(BaseModalComponent) baseModal!: BaseModalComponent;

  renameWorkspaceForm!: FormGroup<{
    workspaceName: FormControl<string | null>;
  }>;

  isLoading = false;
  errorMessage = '';
  private destroy$ = new Subject<void>();

  // Get current workspace from service
  readonly currentWorkspace = computed(() => {
    const viewState = this.keyProjectsService.viewState();
    if (viewState?.selectedWorkspaceId) {
      return this.keyProjectsService.workspaces().find(ws => ws.id === viewState.selectedWorkspaceId) || null;
    }
    return null;
  });

  modalConfig: BaseModalConfig = {
    title: 'Rename Workspace',
    confirmLabel: 'Rename Workspace',
    cancelLabel: 'Cancel',
    confirmVariant: 'primary',
    allowBackdropClose: false
  };

  private readonly formBuilder = inject(FormBuilder);
  private readonly keyProjectsService = inject(KeyProjectsService);

    ngOnInit(): void {
    this.initializeForm();

    // Set initial value from current workspace
    const workspace = this.currentWorkspace();
    if (workspace) {
      this.workspaceNameControl.setValue(workspace.name);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }



  /**
   * Custom validator to check if workspace name already exists
   */
  private workspaceNameExistsValidator(): ValidatorFn {
    return (control: AbstractControl): Record<string, any> | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      const workspaceName = control.value.trim().toLowerCase();
      const existingNames = this.keyProjectsService.workspaces()
        .map(workspace => workspace.name.toLowerCase())
        .filter(name => name !== this.currentWorkspace()?.name.toLowerCase()); // Exclude current name

      if (existingNames.includes(workspaceName)) {
        return { 'workspaceExists': { value: control.value } };
      }

      return null;
    };
  }

  /**
   * Custom validator to check if name is different from original
   */
  private nameChangedValidator(): ValidatorFn {
    return (control: AbstractControl): Record<string, any> | null => {
      if (!control.value) {
        return null;
      }

      const workspace = this.currentWorkspace();
      if (!workspace) {
        return null;
      }

      const newName = control.value.trim();
      const originalName = workspace.name.trim();

      if (newName === originalName) {
        return { 'unchanged': { value: control.value } };
      }

      return null;
    };
  }

  private initializeForm(): void {
    this.renameWorkspaceForm = this.formBuilder.group({
      workspaceName: ['', [
        Validators.required,
        spaceValidator,
        this.workspaceNameExistsValidator(),
        this.nameChangedValidator()
      ]]
    });

    // Update modal config based on form validity
    this.renameWorkspaceForm.statusChanges.subscribe(status => {
      this.modalConfig = {
        ...this.modalConfig,
        confirmDisabled: status === 'INVALID'
      };
    });
  }

  get workspaceNameControl(): FormControl {
    return this.renameWorkspaceForm.get('workspaceName') as FormControl;
  }

  getControlErrors(control: FormControl): any {
    return control.invalid && (control.dirty || control.touched) ? control.errors : null;
  }

  onCancel(): void {
    // Base modal handles the dismissal
  }

  onSubmit(): void {
    if (this.renameWorkspaceForm.valid && !this.isLoading && this.currentWorkspace()) {
      this.renameWorkspace();
    }
  }

  private renameWorkspace(): void {
    const newName = this.workspaceNameControl.value?.trim();
    const workspace = this.currentWorkspace();
    if (!newName || !workspace) return;

    this.isLoading = true;
    this.errorMessage = '';

    this.keyProjectsService.renameWorkspace(workspace.id, newName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedWorkspace) => {
          this.baseModal.closeWithResult(updatedWorkspace);
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.message || 'Failed to rename workspace. Please try again.';
        }
      });
  }
}
